/*
 * PLAYXWORLD ORDER HISTORY PAGE — MONOCHROME BRUTALISM (WHITE BG)
 * Shows user's game top-up order history with status indicators
 * Requires authentication (Firebase/LINE Login)
 */
import Layout from "@/components/Layout";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { Link } from "wouter";
import { motion } from "framer-motion";
import {
  Package,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  ArrowRight,
  Gamepad2,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";

type OrderStatus = "pending" | "processing" | "completed" | "failed";

const STATUS_CONFIG: Record<
  OrderStatus,
  { label: string; icon: React.ElementType; color: string; bgColor: string }
> = {
  completed: {
    label: "สำเร็จ",
    icon: CheckCircle2,
    color: "text-green-700",
    bgColor: "bg-green-100 border-green-300",
  },
  processing: {
    label: "กำลังดำเนินการ",
    icon: Loader2,
    color: "text-yellow-700",
    bgColor: "bg-yellow-100 border-yellow-300",
  },
  pending: {
    label: "รอดำเนินการ",
    icon: Clock,
    color: "text-blue-700",
    bgColor: "bg-blue-100 border-blue-300",
  },
  failed: {
    label: "ไม่สำเร็จ",
    icon: XCircle,
    color: "text-red-700",
    bgColor: "bg-red-100 border-red-300",
  },
};

function StatusBadge({ status }: { status: OrderStatus }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = config.icon;

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 border-2 font-mono text-[10px] tracking-widest uppercase ${config.color} ${config.bgColor}`}
    >
      <Icon
        size={12}
        className={status === "processing" ? "animate-spin" : ""}
      />
      {config.label}
    </span>
  );
}

function OrderCard({
  order,
  index,
}: {
  order: {
    id: number;
    gameId: string;
    gameName: string;
    packageName: string;
    packagePrice: string;
    playerGameId: string;
    serverId: string | null;
    status: string;
    refundAmount?: string | null;
    createdAt: Date;
  };
  index: number;
}) {
  const date = new Date(order.createdAt);
  const formattedDate = date.toLocaleDateString("th-TH", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  const formattedTime = date.toLocaleTimeString("th-TH", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <motion.div
      initial={{ y: 30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{
        duration: 0.3,
        delay: index * 0.05,
        ease: [0.22, 1, 0.36, 1],
      }}
      className="border-3 border-black bg-white hover:bg-black/5 transition-colors"
    >
      {/* Top row: Game name + Status */}
      <div className="flex items-start justify-between p-4 pb-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 border-2 border-black flex items-center justify-center shrink-0">
            <Gamepad2 size={18} />
          </div>
          <div className="min-w-0">
            <h3 className="font-heading font-bold text-sm tracking-tight truncate">
              {order.gameName}
            </h3>
            <div className="font-mono text-[10px] text-black/40 tracking-widest">
              #{String(order.id).padStart(6, "0")}
            </div>
          </div>
        </div>
        <StatusBadge status={order.status as OrderStatus} />
      </div>

      {/* Details grid */}
      <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <div className="font-mono text-[9px] tracking-widest text-black/40 mb-1">
            PACKAGE
          </div>
          <div className="font-mono text-xs text-black/80 truncate">
            {order.packageName}
          </div>
        </div>
        <div>
          <div className="font-mono text-[9px] tracking-widest text-black/40 mb-1">
            PRICE
          </div>
          <div className="font-heading font-bold text-sm">
            THB {parseFloat(order.packagePrice).toLocaleString("th-TH", {
              minimumFractionDigits: 0,
            })}
          </div>
        </div>
        {order.refundAmount && parseFloat(order.refundAmount) > 0 && (
          <div>
            <div className="font-mono text-[9px] tracking-widest text-black/40 mb-1">
              REFUND
            </div>
            <div className="font-heading font-bold text-sm text-red-700">
              THB {parseFloat(order.refundAmount).toLocaleString("th-TH", {
                minimumFractionDigits: 0,
              })}
            </div>
          </div>
        )}
        <div>
          <div className="font-mono text-[9px] tracking-widest text-black/40 mb-1">
            PLAYER ID
          </div>
          <div className="font-mono text-xs text-black/80 truncate">
            {order.playerGameId}
          </div>
        </div>
        <div>
          <div className="font-mono text-[9px] tracking-widest text-black/40 mb-1">
            DATE
          </div>
          <div className="font-mono text-xs text-black/80">
            {formattedDate}
          </div>
          <div className="font-mono text-[10px] text-black/40">
            {formattedTime}
          </div>
        </div>
      </div>

      {/* Refund row (if applicable) */}
      {order.refundAmount && parseFloat(order.refundAmount) > 0 && (
        <div className="px-4 pb-3 border-t-2 border-black/10 pt-3">
          <span className="font-mono text-[9px] tracking-widest text-red-700 font-bold">
            ⚠️ REFUND:{" "}
          </span>
          <span className="font-mono text-xs text-red-700 font-bold">
            THB {parseFloat(order.refundAmount).toLocaleString("th-TH", {
              minimumFractionDigits: 0,
            })}
          </span>
        </div>
      )}

      {/* Server row (if applicable) */}
      {order.serverId && (
        <div className="px-4 pb-3">
          <span className="font-mono text-[9px] tracking-widest text-black/40">
            SERVER:{" "}
          </span>
          <span className="font-mono text-xs text-black/60">
            {order.serverId}
          </span>
        </div>
      )}
    </motion.div>
  );
}

function EmptyState() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
      className="border-3 border-black/30 border-dashed py-16 px-8 text-center"
    >
      <div className="w-20 h-20 border-3 border-black/20 flex items-center justify-center mx-auto mb-6">
        <Package size={36} className="text-black/20" />
      </div>
      <h3 className="font-heading font-bold text-xl tracking-tight mb-2 text-black/60">
        ยังไม่มีรายการสั่งซื้อ
      </h3>
      <p className="font-mono text-xs text-black/40 mb-6 max-w-sm mx-auto">
        เมื่อคุณเติมเกม รายการสั่งซื้อทั้งหมดจะแสดงที่นี่
      </p>
      <Link
        href="/topup"
        className="btn-brutal bg-black text-white inline-flex items-center gap-2 text-sm"
      >
        <Gamepad2 size={16} />
        เติมเกมเลย
        <ArrowRight size={14} />
      </Link>
    </motion.div>
  );
}

function LoginPrompt() {
  const { data: lineLoginData } = trpc.firebase.getLineLoginUrl.useQuery(
    { returnPath: "/orders" },
    { enabled: true }
  );

  return (
    <div className="border-3 border-black/30 border-dashed py-16 px-8 text-center">
      <div className="w-20 h-20 border-3 border-black/20 flex items-center justify-center mx-auto mb-6">
        <AlertTriangle size={36} className="text-black/30" />
      </div>
      <h3 className="font-heading font-bold text-xl tracking-tight mb-2 text-black/60">
        กรุณาเข้าสู่ระบบ
      </h3>
      <p className="font-mono text-xs text-black/40 mb-6">
        เข้าสู่ระบบเพื่อดูประวัติการสั่งซื้อของคุณ
      </p>
      <a
        href={lineLoginData?.url || "#"}
        className="btn-brutal bg-[#06C755] text-white inline-flex items-center gap-2 text-sm border-black"
      >
        <svg width={16} height={16} viewBox="0 0 24 24" fill="currentColor">
          <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
        </svg>
        LINE Login
      </a>
    </div>
  );
}

function OrderStats({
  orders,
}: {
  orders: Array<{ status: string; packagePrice: string }>;
}) {
  const total = orders.length;
  const completed = orders.filter((o) => o.status === "completed").length;
  const totalSpent = orders
    .filter((o) => o.status === "completed")
    .reduce((sum, o) => sum + parseFloat(o.packagePrice), 0);

  return (
    <div className="grid grid-cols-3 gap-4 mb-8">
      {[
        { label: "ทั้งหมด", value: total.toString(), suffix: "รายการ" },
        { label: "สำเร็จ", value: completed.toString(), suffix: "รายการ" },
        {
          label: "ยอดรวม",
          value: `THB ${totalSpent.toLocaleString("th-TH", { minimumFractionDigits: 0 })}`,
          suffix: "",
        },
      ].map((stat) => (
        <div
          key={stat.label}
          className="border-3 border-black p-4 text-center"
        >
          <div className="font-heading font-bold text-2xl md:text-3xl">
            {stat.value}
          </div>
          <div className="font-mono text-[10px] tracking-widest text-black/50 mt-1">
            {stat.label}
            {stat.suffix && (
              <span className="text-black/30 ml-1">{stat.suffix}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Orders() {
  const { isAuthenticated, loading: authLoading } = useAuth();

  const {
    data: orders,
    isLoading,
    refetch,
    isRefetching,
  } = trpc.game.myOrders.useQuery(undefined, {
    enabled: isAuthenticated,
    retry: false,
  });

  const showLoading = authLoading || (isAuthenticated && isLoading);

  return (
    <Layout>
      <section className="bg-white text-black min-h-[60vh]">
        <div className="container py-12 md:py-16">
          {/* Section Header */}
          <div className="flex items-end justify-between mb-8 border-b-3 border-black pb-4">
            <div>
              <span className="font-mono text-xs tracking-widest text-black/50">
                "ORDER HISTORY"
              </span>
              <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl tracking-tight mt-1">
                ประวัติการสั่งซื้อ
              </h1>
            </div>

          </div>

          {/* Content */}
          {showLoading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 size={32} className="animate-spin text-black/40 mb-4" />
              <span className="font-mono text-xs text-black/40 tracking-widest">
                LOADING...
              </span>
            </div>
          ) : !isAuthenticated ? (
            <LoginPrompt />
          ) : !orders || orders.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              {/* Stats Summary */}
              <OrderStats orders={orders} />

              {/* Order List */}
              <div className="flex flex-col gap-4">
                {orders.map((order: any, i: number) => (
                  <OrderCard key={order.id} order={order} index={i} />
                ))}
              </div>

              {/* Bottom CTA */}
              <div className="mt-8 text-center">
                <Link
                  href="/topup"
                  className="btn-brutal bg-black text-white inline-flex items-center gap-2 text-sm"
                >
                  <Gamepad2 size={16} />
                  เติมเกมเพิ่ม
                  <ArrowRight size={14} />
                </Link>
              </div>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}
